import { Helmet } from 'react-helmet-async';
import { Navigate, generatePath, useHref } from 'react-router';

import { ORIGIN } from '@sorare/core/src/config';
import { BLOG_SPORT_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { getPrefix } from '@sorare/core/src/lib/i18n';

import type { getPost } from '#src/components/Post/__generated__/index.graphql.ts';
import { supportedLanguages } from '#src/constants/types.ts';

type Props = {
  post: getPost['post'];
  sportCategory: string;
  language: string;
};

export const PostSeo = ({ post, sportCategory, language }: Props) => {
  const currentPostLanguage = post?.tags?.nodes[0]?.slug;

  const path = generatePath(BLOG_SPORT_SLUG, {
    sport: sportCategory,
    slug: post?.slug || '',
  });

  const pathname = useHref(path);
  const postInEnglish = post?.alternateLanguagePosts?.alternates?.find(
    alternate => alternate?.tags?.edges[0]?.node.name === 'lang-en'
  );
  const canonicalUrl = supportedLanguages.has(language)
    ? `${ORIGIN}${pathname}`
    : `${ORIGIN}${getPrefix('en')}${generatePath(BLOG_SPORT_SLUG, {
        sport: sportCategory,
        slug: postInEnglish?.slug || '',
      })}`;

  if (currentPostLanguage && currentPostLanguage !== `lang-${language}`) {
    const correctLanguagePost = post?.alternateLanguagePosts?.alternates?.find(
      alternate => alternate?.tags?.edges[0]?.node.name === `lang-${language}`
    );
    if (correctLanguagePost?.slug) {
      return (
        <Navigate
          to={generatePath(BLOG_SPORT_SLUG, {
            slug: correctLanguagePost?.slug,
            sport: sportCategory,
          })}
          replace
        />
      );
    }
    if (postInEnglish && currentPostLanguage !== 'lang-en') {
      return (
        <Navigate
          to={generatePath(BLOG_SPORT_SLUG, {
            slug: postInEnglish.slug,
            sport: sportCategory,
          })}
          replace
        />
      );
    }
  }

  const alternates = post?.alternateLanguagePosts?.alternates ?? [];

  const currentLang = currentPostLanguage?.replace(/^lang-/, '');

  return (
    <Helmet>
      {!!sportCategory && <link href={canonicalUrl} rel="canonical" />}
      <link
        hrefLang={currentLang}
        href={`${ORIGIN}${getPrefix(currentLang ?? '')}${generatePath(
          BLOG_SPORT_SLUG,
          {
            sport: sportCategory,
            slug: post?.slug || '',
          }
        )}`}
        rel="alternate"
      />
      {alternates
        ?.filter(alternate => {
          const lang = alternate?.tags?.edges[0]?.node?.name?.replace(
            /^lang-/,
            ''
          );
          return !!lang && supportedLanguages.has(lang);
        })
        .map(alternate => {
          const hrefLang =
            alternate.tags?.edges[0]?.node.name
              ?.replace(/^lang-/, '')
              .toLowerCase() || 'en-us';
          return (
            <link
              key={alternate.slug}
              href={`${ORIGIN}${getPrefix(hrefLang)}${generatePath(
                BLOG_SPORT_SLUG,
                {
                  sport: sportCategory,
                  slug: alternate?.slug || '',
                }
              )}`}
              hrefLang={hrefLang}
              rel="alternate"
            />
          );
        })}
      {!!sportCategory && (
        <link
          href={`${ORIGIN}${generatePath(BLOG_SPORT_SLUG, {
            sport: sportCategory,
            slug: postInEnglish?.slug || post?.slug || '',
          })}`}
          hrefLang="x-default"
          rel="alternate"
        />
      )}
    </Helmet>
  );
};
